import { AnyAction, PayloadAction, Reducer } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer } from 'utils/redux-injectors';
import { GlobalState } from './types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, endpoints, formatErrors } from 'utils/api/endpoints';
import { ApiResponse } from 'types/ApiResponse';

export const initialState: GlobalState = {
  user: JSON.parse(localStorage.getItem('user_data') || '{}'),
  loading: false,
  error: null,
  company: JSON.parse(localStorage.getItem('company_data') || '{}') || null,
};

const slice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
      state.loading = false;
      state.error = null;
      state.company = action.payload.company;

      // Automatically handle localStorage when setting user
      if (action.payload) {
        localStorage.setItem('auth_token', action.payload.api_token);
        localStorage.setItem('company_data', JSON.stringify(action.payload.company));
        localStorage.setItem('user_data', JSON.stringify({
          id: action.payload.id,
          email: action.payload.email,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          role: action.payload.role,
          companyId: action.payload.companyId,
        }));
      }
    },
    setCompany: (state, action: PayloadAction<any>) => {
      state.company = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      state.company = null;
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('company_data');
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    logout: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      state.company = null;
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('company_data');
    },
    initializeAuth: (state) => {
      // Initialize user from localStorage on app start
      const token = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');
      if (token && userData) {
        try {
          state.user = {
            ...JSON.parse(userData),
            api_token: token,
            company: null,
          };
        } catch (error) {
          // Clear invalid data
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          localStorage.removeItem('company_data');
        }
      }
    },
  },
});

export const globalApi = createApi({
  reducerPath: 'globalApi',
  baseQuery,
  tagTypes: ['Global'],
  endpoints: build => ({
    updateCompany: build.mutation<ApiResponse<any>, any>({
      query: (data) => ({
        url: endpoints.updateCompany.url.replace(':id', data.id),
        method: endpoints.updateCompany.method,
        body: data,
      }),
      invalidatesTags: ['Global'],
      transformErrorResponse(baseQueryReturnValue) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),

    getCompany: build.query<ApiResponse<any>, any>({
      query: (data) => {
        // console.log("data", data);
        return {
          url: endpoints.getCompany.url.replace(':id', data.id),
          method: endpoints.getCompany.method,
        }
      },
      transformErrorResponse(baseQueryReturnValue) {
        return formatErrors(baseQueryReturnValue.data);
      }
    }), 

  }),
});

export const { actions: globalActions } = slice;

export const useGlobalSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: globalApi.reducerPath, reducer: globalApi.reducer as Reducer<any, AnyAction> });

  return {
    actions: globalActions,
    useUpdateCompanyMutation: globalApi.useUpdateCompanyMutation,
    useGetCompanyQuery: globalApi.useGetCompanyQuery,
    useLazyGetCompanyQuery: globalApi.useLazyGetCompanyQuery,
  };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useGlobalSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
